/**
 * batch-deliver.js — window.__batchDeliver
 *
 * Hands a BATCH of produced files to the user. Two lanes, one measured rule
 * for choosing between them.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * The default pattern across the batch tools is: keep every result Blob alive,
 * then build one archive over them, then hand that archive to __downloadFile.
 * That pattern has a hard wall, and past the wall it does not merely fail — it
 * fails while REPORTING SUCCESS.
 *
 * Measured 2026-08-25 on the live /heic-converter page, Chromium (see
 * tests/playwright/batch-deliver-wall.mjs, and the scratch sweep it came from):
 *
 *   held results + archive          archive readable?   bytes on disk
 *   ------------------------------  ------------------  --------------
 *    900 MB +  900 MB = 1800 MB     yes                 900 MB    ok
 *   1000 MB + 1000 MB = 2000 MB     yes                1000 MB    ok
 *   1600 MB +  400 MB = 2000 MB     yes                 400 MB    ok
 *   1200 MB + 1200 MB = 2400 MB     NotReadableError    NONE      lost
 *   1073 MB + 1073 MB = 2146 MB     NotReadableError    NONE      lost
 *
 * The wall is not the archive's own size — 1 GB alone downloads fine, and
 * 1.6 GB may be held while a 400 MB archive is delivered. It is the SUM of
 * live blob bytes, and it sits at ~2 GiB, which is exactly Chromium's
 * `max_blob_in_memory_space` = min(2 GiB, physical RAM / 5). On the 8 GB
 * machines this class of user actually has, that budget is 1.6 GB, not 2.
 *
 * Note what is NOT the fix: swapping JSZip for a streaming zipper. fflate was
 * measured head-to-head at 290 x 3.7 MB and produced an equally unreadable
 * archive (both `NotReadableError`, both `landed=NONE`). It still earns its
 * place below, because JSZip additionally materialises the whole archive as
 * one contiguous ArrayBuffer and throws «Array buffer allocation failed» well
 * before the wall (prod: 44 rows / 19 sessions / 90 d on /heic-converter,
 * plus 12 other tools) — but a streaming zipper only removes that error, it
 * does not raise the ceiling.
 *
 * The lane that actually delivers at any size is a FOLDER: write each file as
 * it is produced, hold nothing. Same conclusion /video-frames reached at
 * 472 frames / 1.34 GB. Measured here at 290 x 3.7 MB = 1073 MB:
 * 290/290 files on disk, peak footprint one file, and close() resolving is a
 * real write confirmation — the one thing a blob anchor can never give.
 * `showDirectoryPicker` is Chromium-desktop only, which on /heic-converter is
 * 435 of 469 file-picking sessions in 30 d (92.8 %), so this is the main road
 * and the archive is the fallback, not the other way round.
 *
 * ── Surface ────────────────────────────────────────────────────────────────
 *   __batchDeliver.folderAvailable()        -> bool
 *   __batchDeliver.liveBlobLimitBytes()     -> measured browser budget
 *   __batchDeliver.archiveCeilingBytes()    -> biggest output an archive can carry
 *   __batchDeliver.fitsArchive(totalBytes)  -> bool
 *   __batchDeliver.pickFolder()             -> Promise<sink|null>   null = declined
 *       sink.write(name, blob)              -> Promise<{name, size}>  CONFIRMED
 *       sink.written / sink.bytes / sink.dispose()
 *   __batchDeliver.packZip(items, onProgress?) -> Promise<Blob>
 *       items: [{ name, blob }]; throws Error(code 'BATCH_TOO_BIG') over the ceiling
 *   __batchDeliver.openBatch({zipName, onProgress, slug, total}) -> Promise<batch>
 *       batch.mode 'folder'|'archive'; batch.write(name, blob); batch.finish()
 *       For a tool that PRODUCES files one at a time — the folder lane then
 *       holds one file, not the batch.
 *   __batchDeliver.deliver(items, {zipName, onProgress, slug, archiveOnly}) -> Promise<{via, written}>
 *       THE front door: folder -> archive -> honest refusal, one call. Call it
 *       as the first await in the click. `archiveOnly` — a «Download ZIP»
 *       button: always the archive, never a folder dialog. Entry names are
 *       made distinct on every lane (uniqueName). Everything else above is for a tool
 *       that needs the steps apart (heic-converter arms a folder BEFORE it
 *       converts, so it streams straight to disk and holds nothing).
 */
(function (w) {
    'use strict';
    if (w.__batchDeliver) return;

    /* ⚠️ VERSIONED, and it has to be. These three are inserted BY JavaScript, so
       they never pass through @light — and nginx on the intl shard serves /js
       with `Cache-Control: immutable, max-age=15552000`, a promise that the bytes
       at this URL never change. For a path with no version that promise is false:
       whoever already has the file keeps it for 180 days while every deploy
       reports success. Measured on the live intl domain 2026-09-02 —
       /js/fsa-writable-fallback.js was one of the bare requests still arriving.
       `__jsSrc` is the house primitive for this (layouts/app.blade.php); the
       fallback covers a bare test page where the layout has not run. */
    var _v = function (p) {
        return (typeof w.__jsSrc === 'function') ? w.__jsSrc(p) : p;
    };
    var FSA_SINK_SRC = _v('/js/fsa-writable-fallback.js');
    var FFLATE_SRC = _v('/js/vendor/fflate.min.js');
    var FOLDER_ZIP_SRC = _v('/js/file-transfer/folder-zip.js');
    var NAMES_SRC = _v('/js/output-filename.js');
    var HARD_BLOB_LIMIT = 2 * 1024 * 1024 * 1024;   // Chromium's cap, whatever the RAM

    /* One retry after a pause. These helpers are fetched at the moment the user
       asks for the archive, and a lost request then costs the whole batch:
       prod, 30 d to 2026-09-26 — 12 `zip_fail` «load-failed: /jsv/…» against
       2 894 archives delivered, every one of those URLs answering 200 when
       asked again (a network blip, not a missing file). */
    function loadScript(src) {
        function once() {
            return new Promise(function (resolve, reject) {
                var s = document.createElement('script');
                s.src = src;
                s.onload = resolve;
                s.onerror = function () {
                    try { s.remove(); } catch (e) {}
                    reject(new Error('load-failed: ' + src));
                };
                document.head.appendChild(s);
            });
        }
        return once().catch(function () {
            return new Promise(function (r) { setTimeout(r, 1500); }).then(once);
        });
    }

    function folderAvailable() {
        return typeof w.showDirectoryPicker === 'function';
    }

    /**
     * How many bytes of live Blob this browser will hold before blobs stop
     * being readable. Chromium: min(2 GiB, physical RAM / 5). navigator
     * .deviceMemory is spec-clamped to 8, so on big machines this UNDER-states
     * the budget — which is the safe direction: a refusal costs a folder pick,
     * a wrong allowance costs the user their whole batch.
     */
    function liveBlobLimitBytes() {
        var gb = Number(w.navigator && w.navigator.deviceMemory) || 4;
        return Math.min(HARD_BLOB_LIMIT, (gb * 1024 * 1024 * 1024) / 5);
    }

    /**
     * Two archive builders, two different ceilings — and the difference is the
     * whole reason this function is not `budget / 2` any more.
     *
     * A MATERIALISING zipper (fflate, JSZip) reads every source blob into an
     * ArrayBuffer and accumulates the output, so results and archive are alive
     * at once and the deliverable output really is half the budget.
     *
     * A COMPOSITE zipper writes only headers and offsets and returns a Blob
     * assembled FROM REFERENCES to the source blobs — the bytes are never
     * copied. That is what public/js/file-transfer/folder-zip.js has always
     * done (STORE only, exactly like fflate's ZipPassThrough here, so nothing
     * is given up), and it moves the wall from half the budget to nearly all
     * of it.
     *
     * Measured 2026-08-31, headless Chromium, budget 2048 MB, one context per
     * case, readability checked at the HEAD (local-file-header signature), the
     * MIDDLE (1 MB inside file data — a tail-only read proves nothing about a
     * blob made of references) and the TAIL:
     *
     *   held MB   materialising (fflate)   composite (FolderZip)
     *   1000      ok        2.4 s          ok         5.5 s
     *   1400      over its ceiling         ok         9.7 s
     *   1800      over its ceiling         ok        10.6 s
     *   1950      over its ceiling         ok        13.9 s
     *   2000      over its ceiling         NotReadableError
     *
     * So: composite reaches ~95 % of the budget, materialising exactly 50 %,
     * and materialising is ~2.3x faster per MB. Hence the ladder in packZip():
     * the fast builder while the batch fits it, the composite one above that,
     * an honest refusal past the budget. Nobody pays for a ceiling they are
     * nowhere near, and nobody is refused a batch that can be delivered.
     *
     * Why this mattered: /equalizer hands back 17 mastered WAVs (609 MB) on a
     * 4 GB laptop. Budget 819 MB. Halved ceiling = 409 MB — the tool would have
     * REFUSED a batch the composite lane delivers with room to spare. A fix
     * that halves a ceiling is a regression wearing a fix's clothes.
     */
    var COMPOSITE_SAFETY = 0.9;     // 1950 MB of 2048 still delivered; 2000 did not
    function materialiseCeilingBytes() { return Math.floor(liveBlobLimitBytes() / 2); }
    function archiveCeilingBytes() { return Math.floor(liveBlobLimitBytes() * COMPOSITE_SAFETY); }
    function fitsArchive(totalBytes) { return (totalBytes || 0) <= archiveCeilingBytes(); }

    /*
     * ONE owner of «two entries never share a name»: __outputName.distinct.
     * Loaded on demand (ensureNames) before anything is named, so a tool that
     * never included output-filename.js gets the same rule as one that did.
     *
     * Why it matters (2026-09-26): /split-pdf packed its own JSZip, and JSZip's
     * file() REPLACES an entry of the same name — 18 of 51 archives in 90 days
     * held only the last part. An audit found ~20 more tools packing their own
     * archive with names built from the input («logo.jpg» + «logo.png» → two
     * «logo-no-bg.png»), five of them with a home-made de-dup, each broken in
     * its own way (case, the generated name never marked as used). They now
     * pack through this file, and this is where the rule lives.
     *
     * Only the LAST path segment is made distinct: «photo/Instagram/post.jpg»
     * stays in its folder as «post (2).jpg» instead of being flattened by the
     * separator-to-dash cleaning. Case-insensitive, like Windows and macOS.
     */
    function ensureNames() {
        return w.__outputName ? Promise.resolve() : loadScript(NAMES_SRC).catch(function () {});
    }
    function uniqueName(name, used) {
        var slash = name.lastIndexOf('/');
        var dir = slash >= 0 ? name.slice(0, slash + 1) : '';
        var leaf = slash >= 0 ? name.slice(slash + 1) : name;
        var dirLow = dir.toLowerCase();
        var view = {
            has: function (n) { return Object.prototype.hasOwnProperty.call(used, dir + n); },
            forEach: function (fn) {
                Object.keys(used).forEach(function (k) {
                    if (k.slice(0, dir.length).toLowerCase() === dirLow && k.indexOf('/', dir.length) < 0) fn(k.slice(dir.length), k.slice(dir.length));
                });
            },
        };
        if (w.__outputName && typeof w.__outputName.distinct === 'function') {
            // Land-able first: the byte cap (a 230-letter Cyrillic title is 460
            // bytes, and ext4/Android refuse anything past 255 — the entry is in
            // the archive and cannot be unpacked) and the characters Windows
            // refuses. A name that is already fine comes back unchanged.
            var p = w.__outputName.split(leaf);
            var fit = w.__outputName.join(p.base, p.ext) || leaf;
            return dir + w.__outputName.distinct(fit, view);
        }
        // Names module failed to load: same rule, without its filename cleaning.
        var taken = {};
        view.forEach(function (k) { taken[k.toLowerCase()] = 1; });
        if (!taken[leaf.toLowerCase()]) return name;
        var dot = leaf.lastIndexOf('.');
        var base = dot > 0 ? leaf.slice(0, dot) : leaf;
        var ext = dot > 0 ? leaf.slice(dot) : '';
        var i = 2, cand;
        do { cand = base + ' (' + (i++) + ')' + ext; } while (taken[cand.toLowerCase()] && i < 100000);
        return dir + cand;
    }

    /* A file whose write close() confirmed is a file the person HAS — the same
       fact as a download, so it is reported the way the download door reports
       one: `file_download` with the verdict `ok:1`, the extension and the size
       (__downloadFile's `_ev` in timbrica-core.js). Until 2026-10-22 the folder
       lane said nothing at all, and everything that asks «did the person get
       the result» read a folder delivery as nothing: refund request №51 —
       26 HEIC → JPG written into the buyer's folder, the refund panel «забрал
       0», the п. 1.5.1 count 0 for the same reason. Per file, not per batch:
       the readers count distinct results by `ext|sizeKB`. No `file-downloaded`
       event: its listeners are the «file saved» toast and the download
       history, and a folder the person chose is neither. */
    function delivered(name, blob) {
        // The core's door when the page has it (every site page); this copy
        // serves a bare page and the unit test, which load the primitive alone.
        if (typeof w.__deliveredToDisk === 'function') { w.__deliveredToDisk(name, blob, 'folder'); return; }
        try {
            if (!w.__ae) return;
            var m = /\.([A-Za-z0-9]{1,8})$/.exec(String(name || ''));
            var slug = w.__TOOL_SLUG || ((document.querySelector('meta[name="tool-slug"]') || {}).content) || '';
            w.__ae('feature_use', {
                slug: slug, f: 'file_download', via: 'folder', src: 'disk', ok: 1,
                ext: m ? m[1].toLowerCase() : '',
                sizeKB: blob ? Math.round(blob.size / 1024) : 0,
            });
        } catch (e) {}
    }

    /**
     * Opens the folder picker and returns a sink, or null when the user closes
     * the dialog (an informed decision, not a failure — the caller must not
     * fall back to a download storm).
     *
     * MUST be called straight from a click: showDirectoryPicker needs user
     * activation, and awaiting anything first spends it. The sink module is
     * therefore loaded AFTER the picker resolves, never before.
     */
    function pickFolder() {
        var dir;
        try {
            dir = w.showDirectoryPicker({ mode: 'readwrite' });
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(dir).then(function (handle) {
            return Promise.all([w.__FsaSink ? Promise.resolve() : loadScript(FSA_SINK_SRC), ensureNames()]).then(function () {
                if (!w.__FsaSink) throw new Error('FSA_SINK_MISSING');
                var sink = w.__FsaSink.create(handle, {});
                var used = Object.create(null);
                var api = {
                    written: 0,
                    bytes: 0,
                    /** Resolves only once close() confirmed the write. */
                    write: function (name, blob) {
                        var finalName = uniqueName(name, used);
                        used[finalName] = 1;
                        return sink.openFile(finalName).then(function (writer) {
                            return writer.write(blob).then(function () { return writer.close(); })
                                .catch(function (err) {
                                    // A failed write (disk full, device pulled)
                                    // must not leave an empty file named like a
                                    // result — on a car's flash drive that is a
                                    // track that shows up and never plays.
                                    var d = writer.discard ? writer.discard() : null;
                                    return Promise.resolve(d).catch(function () {}).then(function () { throw err; });
                                });
                        }).then(function () {
                            api.written++;
                            api.bytes += (blob && blob.size) || 0;
                            delivered(finalName, blob);
                            return { name: finalName, size: (blob && blob.size) || 0 };
                        });
                    },
                    dispose: function () { try { if (sink.dispose) sink.dispose(); } catch (e) {} },
                    // The folder the person picked, for a caller that has to
                    // look before writing (what is already there, which
                    // numbers are taken). Read-only use; writes go through write().
                    dir: handle,
                };
                return api;
            });
        }, function (err) {
            if (err && err.name === 'AbortError') return null;
            throw err;
        });
    }

    /**
     * Streamed ZIP. Refuses over the ceiling BEFORE doing the work, because
     * past it the archive is built, reported saved, and never lands.
     * STORE only: this packs already-compressed media (jpg/png/webp/mp3/mp4),
     * where DEFLATE costs seconds for ~0 %.
     */
    /**
     * The composite lane: ONE zip encoder in the codebase, not two. It lives in
     * file-transfer/folder-zip.js because that is where it was written; it is
     * loaded on demand here because the archive lane needs no user activation
     * (the folder picker does, and that decision was already made above).
     */
    function zipModule() {
        return (w.__FileTransfer && w.__FileTransfer.FolderZip ? Promise.resolve() : loadScript(FOLDER_ZIP_SRC)).then(function () {
            var FZ = w.__FileTransfer && w.__FileTransfer.FolderZip;
            if (!FZ) { var e = new Error('FOLDER_ZIP_MISSING'); e.code = 'ZIP_MODULE'; throw e; }
            return FZ;
        }, function (err) { var e = new Error(String((err && err.message) || err)); e.code = 'ZIP_MODULE'; throw e; });
    }
    /* onPack(fraction) — how far the checksum pass over the sources is, 0…1. */
    function compositeZip(items, onProgress, onPack) {
        return Promise.all([zipModule(), ensureNames()]).then(function (got) {
            var FZ = got[0];
            var used = Object.create(null);
            var files = items.map(function (it, i) {
                var name = uniqueName(it.name || ('file-' + (i + 1)), used);
                used[name] = 1;
                // `new File([blob], name)` does NOT copy the bytes: a blob part
                // is a reference, which is the very mechanism this lane rests
                // on (measured — the composite archive reaches ~95 % of the
                // budget, which would be impossible with a copy). Mutating the
                // caller's blob with a `.path` would be cheaper still but breaks
                // when the same blob is handed over twice under two names.
                var file = new File([it.blob], name, { type: it.blob.type });
                // FolderZip reads `.path` first. Set it explicitly: a slash in
                // a File name is kept by Chromium, but that is an engine's
                // choice, not a promise, and a folder inside the archive
                // (/social-photo-resize: photo/Instagram/…) must survive.
                try { file.path = name; } catch (e) {}
                return file;
            });
            var wrapOpts = { name: 'batch.zip' };
            if (onPack) wrapOpts.onProgress = function (done, all) { try { onPack(all > 0 ? Math.min(1, done / all) : 1); } catch (e) {} };
            return FZ.wrap(files, wrapOpts).then(function (z) {
                if (onProgress) { try { onProgress(items.length, items.length); } catch (e) {} }
                return z.slice(0, z.size);
            });
        });
    }

    /*
     * ── One archive: tried and verified, not predicted (2026-10-14) ─────────
     *
     * The ceiling above is a MODEL of Chromium's blob store fed with
     * navigator.deviceMemory. It was the gate of this function until a bug
     * report from Firefox («2.67 GB passen nicht in ein Archiv»), where the
     * model has nothing to stand on — Firefox reports no deviceMemory, so a
     * 4 GB machine is assumed and the gate closes at 737 MB. So the question
     * was put to the browsers instead (tests/playwright/
     * batch-archive-single-probe.mjs, a fresh browser per case, the archive
     * that LANDED opened and every entry checked by size and CRC-32):
     *
     *                                       1.9 GB    2.6 GB    3.9 GB
     *   Firefox 148   files on its disk     intact    intact    intact
     *   Firefox 148   files in memory       intact    intact    intact
     *   Chromium 147  files on its disk     intact    intact    intact
     *   Chromium 147  files in memory       intact    sources unreadable (both)
     *
     * Two things follow. The model is low by a factor of two to five wherever
     * the files are not Chromium memory blobs — the streaming writers leave
     * theirs on disk. And the one real wall announces itself: past it the
     * SOURCE blobs cannot be read, and the composite builder reads every byte
     * of every source for its checksums. So on the engines that were measured
     * the archive is simply attempted: it either builds — and then it landed
     * intact in every measured case — or the read fails and the answer is the
     * honest refusal, with nothing handed over. What is left to refuse up front
     * is the FORMAT (4 GiB, 65 534 entries — FolderZip.fits), and that is what
     * the parts below are for.
     *
     * An engine nobody measured (WebKit: real Safari cannot be driven here)
     * keeps the model as its gate, and gets parts above it.
     */
    function measuredEngine() {
        try {
            var nav = w.navigator || {}, ua = String(nav.userAgent || '');
            // Chrome, Edge, Opera, Yandex, Samsung. On iOS every browser is
            // WebKit and says «Apple Computer, Inc.» here.
            if (nav.vendor === 'Google Inc.') return 'chromium';
            if (!nav.vendor && /Gecko\/\d/.test(ua) && /Firefox\/\d/.test(ua)) return 'gecko';
        } catch (e) {}
        return '';
    }
    function tooBig(total, ceiling, why) {
        var big = new Error('BATCH_TOO_BIG');
        big.code = 'BATCH_TOO_BIG';
        big.totalBytes = total;
        big.ceilingBytes = ceiling;
        big.why = why;
        return big;
    }

    /** opts.onPack(fraction) — progress of the checksum pass. Rejects with
     *  code BATCH_TOO_BIG (why: 'model' | 'entries' | 'entry' | 'size' — the
     *  batch has to go out another way) or BATCH_UNREADABLE (a source the
     *  browser can no longer read: nothing can carry that). */
    function packZip(items, onProgress, opts) {
        opts = opts || {};
        var total = 0;
        for (var i = 0; i < items.length; i++) total += (items[i].blob && items[i].blob.size) || 0;
        if (!measuredEngine() && !fitsArchive(total)) return Promise.reject(tooBig(total, archiveCeilingBytes(), 'model'));
        // The composite lane is the ONLY lane for a batch of Blobs now
        // (2026-09-05). The materialising builder was kept below its own
        // ceiling because it is ~2.3× faster per MB, but it holds the WHOLE
        // archive in the JS heap as Uint8Array chunks before the Blob exists —
        // and that is where «Array buffer allocation failed» comes from: a
        // /heic-converter session hit it on 2026-09-03, ten days after the
        // ceiling landed, on a batch the ceiling had allowed. A reference
        // archive costs no heap at all (the bytes are never copied), so the
        // speed it gives up (5.5 s/GB against 2.4) buys the one thing the
        // archive lane is for: an archive that exists when it says it does.
        // The materialising builder stays as the fallback for a page where
        // the composite module cannot load.
        return compositeZip(items, onProgress, opts.onPack).catch(function (err) {
            // More than the classic format holds — the caller cuts it into parts.
            if (err && err.code === 'ZIP_TOO_BIG') throw tooBig(total, 0, err.why || 'size');
            // What a read that failed looks like: Chromium says NotReadableError
            // or NotFoundError, Firefox AbortError (all three measured here).
            // Anything else is the encoder itself not working on this engine,
            // and that keeps the old way out — the heap builder below.
            var readFailed = !!(err && /^(NotReadableError|NotFoundError|AbortError|InvalidStateError|SecurityError|UnknownError)$/.test(String(err.name || '')));
            if (readFailed) {
                // The encoder was there and a source could not be read (Chromium
                // past its blob store, a file deleted under the page). No other
                // builder reads what this one could not.
                var gone = new Error('BATCH_UNREADABLE');
                gone.code = 'BATCH_UNREADABLE';
                gone.totalBytes = total;
                gone.cause = String((err && (err.name + ': ' + err.message)) || err).slice(0, 120);
                throw gone;
            }
            try { if (w.__ae) w.__ae('feature_use', { f: 'batch_zip_composite_unavailable', err: String((err && err.message) || err).slice(0, 60) }); } catch (e) {}
            if (total > materialiseCeilingBytes()) {
                // No composite encoder and past the heap builder's ceiling: the
                // batch is genuinely undeliverable as one archive, and saying
                // so beats handing over an archive that reports success and
                // lands nothing.
                var big = tooBig(total, materialiseCeilingBytes(), 'model');
                big.cause = String((err && err.message) || err);
                big.noComposite = true;
                throw big;
            }
            return materialisingZip(items, onProgress);
        });
    }

    function materialisingZip(items, onProgress) {
        return Promise.all([w.fflate ? Promise.resolve() : loadScript(FFLATE_SRC), ensureNames()]).then(function () {
            if (!w.fflate) throw new Error('FFLATE_MISSING');
            var used = Object.create(null);
            return new Promise(function (resolve, reject) {
                var chunks = [];
                var zip = new w.fflate.Zip(function (err, chunk, final) {
                    if (err) { reject(err); return; }
                    if (chunk && chunk.length) chunks.push(chunk);
                    if (final) resolve(new Blob(chunks, { type: 'application/zip' }));
                });
                var idx = 0;
                (function next() {
                    if (idx >= items.length) { try { zip.end(); } catch (e) { reject(e); } return; }
                    var it = items[idx++];
                    var name = uniqueName(it.name || ('file-' + idx), used);
                    used[name] = 1;
                    it.blob.arrayBuffer().then(function (buf) {
                        var entry = new w.fflate.ZipPassThrough(name);
                        zip.add(entry);
                        entry.push(new Uint8Array(buf), true);
                        if (onProgress) { try { onProgress(idx, items.length); } catch (e) {} }
                        next();
                    }, reject);
                })();
            });
        });
    }

    /**
     * ── deliver(items, opts) — the whole ladder in one call ────────────────
     *
     * Every "save all" button needs the same three steps in the same order:
     * folder if the browser has one, one archive if not, an honest refusal when
     * the archive cannot carry the batch. Before this existed each tool wrote
     * that ladder again — and the ones that never wrote it at all fell back to
     * the thing that looks easiest and is worst: a loop of downloads. Measured
     * on real Chrome 152, a browser grants ONE download per user interaction;
     * the rest are dropped in silence AND the tab stops delivering downloads
     * for the whole origin afterwards (see __dlGesture in timbrica-core.js).
     * Prod, 14 d: 476 sessions threw away 4 674 finished files that way, and
     * 5 900 later downloads across 60 OTHER tools landed in a tab one of those
     * loops had already killed.
     *
     * So the loop is not a lane, and this call is the only front door a batch
     * needs.
     *
     *   items : [{ name, blob }]
     *   opts  : { zipName, onProgress(done,total), slug }
     *   -> { via: 'folder'|'zip'|'declined'|'refused'|'empty', written }
     *
     * ⚠️ MUST be called as the FIRST await inside the click handler:
     * showDirectoryPicker needs the activation that handler is holding, and
     * awaiting anything before it spends that activation. Have the items ready
     * before the call, not inside it.
     *
     * A closed folder dialog is an informed decision, so it degrades to the
     * archive rather than to a download storm; a batch too big for an archive
     * says so, and says which lane would carry it.
     */
    function deliverMsg(key, a, n) {
        var L = ((w.__LOCALE || 'en') + '').substring(0, 2);
        var M = {
            too_big: {
                ru: 'Файлы (' + a + ') не помещаются в один архив — этот браузер столько за раз не отдаёт. Сохраните их в папку (Chrome, Edge или Opera на компьютере) или выберите меньше файлов.',
                en: 'The files (' + a + ') do not fit into one archive — this browser cannot hand over that much at once. Save them to a folder (desktop Chrome, Edge or Opera) or pick fewer files.',
                de: 'Die Dateien (' + a + ') passen nicht in ein Archiv — so viel gibt dieser Browser nicht auf einmal heraus. Speichern Sie sie in einen Ordner (Chrome, Edge oder Opera am Computer) oder wählen Sie weniger Dateien.',
                es: 'Los archivos (' + a + ') no caben en un solo archivo comprimido: este navegador no entrega tanto de una vez. Guárdalos en una carpeta (Chrome, Edge u Opera de escritorio) o elige menos archivos.',
                fr: 'Les fichiers (' + a + ') ne tiennent pas dans une seule archive — ce navigateur n’en délivre pas autant d’un coup. Enregistrez-les dans un dossier (Chrome, Edge ou Opera sur ordinateur) ou choisissez moins de fichiers.',
                pt: 'Os arquivos (' + a + ') não cabem em um único arquivo compactado — este navegador não entrega tudo de uma vez. Salve-os em uma pasta (Chrome, Edge ou Opera no computador) ou escolha menos arquivos.',
                id: 'Berkas (' + a + ') tidak muat dalam satu arsip — peramban ini tidak bisa menyerahkan sebanyak itu sekaligus. Simpan ke folder (Chrome, Edge, atau Opera di komputer) atau pilih lebih sedikit berkas.',
                ko: '파일(' + a + ')이 하나의 압축 파일에 들어가지 않습니다 — 이 브라우저는 한 번에 그만큼 넘겨주지 못합니다. 폴더에 저장하거나(데스크톱 Chrome·Edge·Opera) 파일 수를 줄이세요.',
                ar: 'الملفات (' + a + ') لا تتسع في أرشيف واحد — هذا المتصفح لا يسلّم هذا القدر دفعة واحدة. احفظها في مجلد (Chrome أو Edge أو Opera على الحاسوب) أو اختر ملفات أقل.',
                uk: 'Файли (' + a + ') не вміщуються в один архів — цей браузер стільки за раз не віддає. Збережіть їх у теку (Chrome, Edge або Opera на комп’ютері) чи оберіть менше файлів.',
                tr: 'Dosyalar (' + a + ') tek bir arşive sığmıyor — bu tarayıcı bir seferde bu kadarını vermiyor. Bunları bir klasöre kaydedin (bilgisayarda Chrome, Edge veya Opera) veya daha az dosya seçin.'
            },
            // The button named here is the one on the held-files bar (__dlHold).
            parts: {
                ru: 'Файлы (' + a + ') не помещаются в один архив, поэтому сохраняются частями: их ' + n + '. Первая уже сохраняется — для каждой следующей нажмите «Сохранить» внизу.',
                en: 'The files (' + a + ') do not fit into one archive, so they come in ' + n + ' parts. The first is being saved now — press “Save” below for each of the others.',
                de: 'Die Dateien (' + a + ') passen nicht in ein Archiv und werden deshalb in ' + n + ' Teilen gespeichert. Der erste Teil wird jetzt gespeichert — für jeden weiteren drücken Sie unten auf „Speichern“.',
                es: 'Los archivos (' + a + ') no caben en un solo archivo comprimido, así que se guardan en ' + n + ' partes. La primera ya se está guardando; para cada una de las demás, presiona «Guardar» abajo.',
                fr: 'Les fichiers (' + a + ') ne tiennent pas dans une seule archive : ils sont enregistrés en ' + n + ' parties. La première est en cours d’enregistrement — pour chacune des suivantes, appuyez sur « Enregistrer » en bas.',
                pt: 'Os arquivos (' + a + ') não cabem em um único arquivo compactado, por isso são salvos em ' + n + ' partes. A primeira já está sendo salva — para cada uma das outras, pressione “Salvar” abaixo.',
                id: 'Berkas (' + a + ') tidak muat dalam satu arsip, jadi disimpan dalam ' + n + ' bagian. Bagian pertama sedang disimpan — untuk tiap bagian berikutnya, tekan “Simpan” di bawah.',
                ko: '파일(' + a + ')이 하나의 압축 파일에 들어가지 않아 ' + n + '개로 나누어 저장합니다. 첫 번째는 지금 저장되고 있습니다 — 나머지는 아래의 “저장”을 눌러 하나씩 받으세요.',
                ar: 'الملفات (' + a + ') لا تتسع في أرشيف واحد، لذلك تُحفظ في ' + n + ' أجزاء. الجزء الأول يُحفظ الآن — ولكل جزء آخر اضغط «حفظ» في الأسفل.',
                uk: 'Файли (' + a + ') не вміщуються в один архів, тому зберігаються частинами: їх ' + n + '. Перша вже зберігається — для кожної наступної натисніть «Зберегти» внизу.'
            },
            // Every file travels as itself (deliverInParts, «one file a part»).
            singles: {
                ru: 'Файлов: ' + n + ' (' + a + ') — браузер сохраняет их по одному. Первый уже сохраняется — для каждого следующего нажмите «Сохранить» внизу.',
                en: n + ' files (' + a + ') — the browser saves them one at a time. The first is being saved now — press “Save” below for each of the others.',
                de: n + ' Dateien (' + a + ') — der Browser speichert sie einzeln. Die erste wird jetzt gespeichert — für jede weitere drücken Sie unten auf „Speichern“.',
                es: n + ' archivos (' + a + '): el navegador los guarda de uno en uno. El primero ya se está guardando; para cada uno de los demás, presiona «Guardar» abajo.',
                fr: n + ' fichiers (' + a + ') — le navigateur les enregistre un par un. Le premier est en cours d’enregistrement — pour chacun des suivants, appuyez sur « Enregistrer » en bas.',
                pt: n + ' arquivos (' + a + ') — o navegador os salva um de cada vez. O primeiro já está sendo salvo — para cada um dos outros, pressione “Salvar” abaixo.',
                id: n + ' berkas (' + a + ') — peramban menyimpannya satu per satu. Berkas pertama sedang disimpan — untuk tiap berkas berikutnya, tekan “Simpan” di bawah.',
                ko: '파일 ' + n + '개(' + a + ') — 브라우저는 하나씩 저장합니다. 첫 번째는 지금 저장되고 있습니다 — 나머지는 아래의 “저장”을 눌러 하나씩 받으세요.',
                ar: n + ' ملفات (' + a + ') — يحفظها المتصفح واحدًا تلو الآخر. الأول يُحفظ الآن — ولكل ملف آخر اضغط «حفظ» في الأسفل.',
                uk: 'Файлів: ' + n + ' (' + a + ') — браузер зберігає їх по одному. Перший уже зберігається — для кожного наступного натисніть «Зберегти» внизу.'
            }
        };
        var g = M[key] || {};
        return g[L] || g.en || '';
    }

    /*
     * ── A batch too big for ONE archive goes out in PARTS ──────────────────
     *
     * Until 2026-10-13 the archive lane answered such a batch with a refusal —
     * and by then the tool had already produced every file. Bug report
     * 2026-10-06, /de/channel-converter, Firefox (no folder picker there): a
     * whole album converted, then «Die Dateien (2.67 GB) passen nicht in ein
     * Archiv», the files dropped with the lane's `held`, and one line from the
     * person: «wo sind meine konvertierten files». The advice in that toast
     * (another browser) is right for the NEXT batch and useless for this one.
     *
     * The ceiling is about one archive, not about the files: each of them is a
     * blob that exists. So the batch is cut, in order, into runs that each fit
     * the ceiling; every run becomes its own archive (a file bigger than the
     * ceiling is a run of its own and travels as itself — no archive could
     * carry it, and zipping a lone 3 GB recording only adds a copy to unpack).
     * The first goes out now, the others wait on the held-files bar, one press
     * each: a browser grants one download per gesture (__dlGesture).
     *
     * Which ceiling: on a measured engine one archive is attempted first (see
     * packZip), so parts only begin where the zip FORMAT ends — 4 GiB or
     * 65 534 entries a part. On an engine nobody measured it is the model.
     *
     * Nothing here is taken on trust. compositeZip() reads every byte of every
     * entry for its CRC, so a blob the browser can no longer read fails the
     * build — and ALL parts are built before the first is handed over. Either
     * the whole batch is deliverable or the answer is the old honest refusal;
     * never half of one.
     *
     * ── When the ceiling carries ONE file a part, there are no archives ──
     * (2026-10-29). Support chat 728, desktop Safari, Premium bought for this:
     * /video-trim «Разделить» cut a 5.5 GB camera take into nine 630 MB mp4s.
     * WebKit's model ceiling is 737 MB, so every run held exactly one file —
     * and every one was zipped: nine «…-partKofN.zip», each holding one mp4
     * to unpack, built by a CRC pass over all 5.6 GB that ran minutes with
     * nothing on the screen (reproduced in Playwright WebKit on a 1.29 GB take:
     * parts made in 8.5 s, then 77 s of silence, then three one-entry zips).
     * An archive of one entry is the copy this lane already refuses for a file
     * bigger than the ceiling; when NO run holds two files, each file travels
     * as itself, one press each, and nothing is read but its head, middle and
     * tail. A batch with real multi-file runs keeps its numbered archives (a
     * lone file after four «part K of 5» still reads as part 5).
     */
    function splitParts(items, limit) {
        var parts = [], run = [], bytes = 22;
        var cost = limit.cost || function (it) { return (it.blob && it.blob.size) || 0; };
        items.forEach(function (it) {
            var c = cost(it);
            if (run.length && (bytes + c > limit.bytes || run.length >= limit.entries)) { parts.push(run); run = []; bytes = 22; }
            run.push(it);
            bytes += c;
        });
        if (run.length) parts.push(run);
        return parts;
    }

    /* Head, middle and tail of a file that travels as itself. A tail-only read
       proves nothing about a blob made of references (measured 2026-08-31). */
    function readable(blob) {
        var size = (blob && blob.size) || 0;
        if (!size || typeof blob.slice !== 'function' || typeof blob.arrayBuffer !== 'function') return Promise.resolve(true);
        var at = [0, Math.max(0, Math.floor(size / 2) - 8), Math.max(0, size - 16)];
        return at.reduce(function (p, o) {
            return p.then(function (ok) {
                if (!ok) return false;
                return blob.slice(o, Math.min(size, o + 16)).arrayBuffer()
                    .then(function (b) { return b.byteLength > 0; }, function () { return false; });
            });
        }, Promise.resolve(true));
    }

    /** -> Promise<{parts, bare}> once every part is built and handed over, or
     *  {failed: 'unreadable'|'unavailable', cause} when the batch cannot go out
     *  this way (the caller then refuses — and says WHICH of the two it was:
     *  a source the browser stopped reading is not a batch too big). */
    function deliverInParts(items, zipName, onPack) {
        var hold = w.__dlHold;
        if (!hold || !hold.singles || typeof hold.add !== 'function' || !w.__downloadFile) return Promise.resolve({ failed: 'unavailable', cause: 'no_hold' });
        var base = String(zipName || 'files.zip').replace(/\.zip$/i, ''), built = [], n = 0, bare = 0;
        var total = 0, done = 0;
        items.forEach(function (it) { total += (it.blob && it.blob.size) || 0; });
        var chain = Promise.all([zipModule(), ensureNames()]).then(function (got) {
            var FZ = got[0];
            // The same per-entry count the writer's own fits() uses.
            var entry = function (it) { return FZ.cost({ size: (it.blob && it.blob.size) || 0, name: it.name }); };
            var limit = measuredEngine()
                ? { bytes: FZ.MAX_BYTES, entries: FZ.MAX_ENTRIES, cost: entry }
                : { bytes: archiveCeilingBytes(), entries: FZ.MAX_ENTRIES };
            var parts = splitParts(items, limit);
            n = parts.length;
            var onePerPart = parts.every(function (p) { return p.length === 1; });
            var seq = Promise.resolve();
            parts.forEach(function (part, k) {
                seq = seq.then(function () {
                    var partBytes = 0;
                    part.forEach(function (it) { partBytes += (it.blob && it.blob.size) || 0; });
                    var before = done;
                    var report = function (f) { if (onPack && total > 0) { try { onPack(Math.min(1, (before + f * partBytes) / total)); } catch (e) {} } };
                    // A file travels bare when no archive could carry it, or when no
                    // archive would carry more than it (see «one file a part» above).
                    // A short last run among real archives is still «part 5 of 5» —
                    // a loose track after four numbered archives reads as a part
                    // that went missing.
                    if (part.length === 1 && (onePerPart || (limit.cost ? limit.cost(part[0]) : partBytes) > limit.bytes)) {
                        return readable(part[0].blob).then(function (ok) {
                            if (!ok) { var gone = new Error('PART_UNREADABLE'); gone.name = 'NotReadableError'; throw gone; }
                            var leaf = String(part[0].name || 'file').split('/').pop();
                            built.push({ blob: part[0].blob, name: leaf });
                            bare++;
                            done = before + partBytes; report(1);
                        });
                    }
                    return compositeZip(part, null, report).then(function (zip) {
                        built.push({ blob: zip, name: n > 1 ? base + '-part' + (k + 1) + 'of' + n + '.zip' : base + '.zip' });
                        done = before + partBytes;
                    });
                });
            });
            return seq;
        });
        return chain.then(function () {
            var first = built[0], rest = built.slice(1);
            var park = function (b) { hold.add(b.blob, b.name, { single: true }); };
            // This interaction's one download may be spent already (the person
            // saved something else while the batch was cooking): then the first
            // part waits its turn on the bar like the others, in order.
            var taken = false;
            try { taken = !!(w.__dlGesture && w.__dlGesture.taken()); } catch (e) {}
            if (taken) { built.forEach(park); return { parts: n, bare: bare }; }
            return Promise.resolve(w.__downloadFile(first.blob, first.name, { src: 'batch_deliver' })).then(function (ok) {
                var held = false;
                try { held = hold.peek().some(function (it) { return it.blob === first.blob; }); } catch (e) {}
                if (ok === false && !held) park(first);
                rest.forEach(park);
                return { parts: n, bare: bare };
            });
        }, function (err) {
            // The same test packZip applies: a read that failed is a source the
            // browser no longer reads (support chat 728: Safari stopped reading
            // the picked 5.5 GB take mid-batch), not a batch that is too big.
            var readFailed = !!(err && /^(NotReadableError|NotFoundError|AbortError|InvalidStateError|SecurityError|UnknownError)$/.test(String(err.name || '')));
            return { failed: readFailed ? 'unreadable' : 'unavailable', cause: String((err && (err.name + ': ' + err.message)) || err).slice(0, 120) };
        });
    }

    /**
     * openBatch(opts) — the same ladder for a tool that PRODUCES its files one
     * at a time (encode → write → encode → write). The folder lane then holds
     * exactly one file at a time instead of the whole batch, which is the only
     * reason /heic-converter can deliver 290 photos and 1.07 GB at all.
     *
     *   batch.mode              'folder' | 'archive'
     *   batch.dir               folder lane: the picked FileSystemDirectoryHandle (look, don't write)
     *   batch.write(name, blob) -> Promise   (folder: a CONFIRMED disk write)
     *   batch.finish()          -> Promise<{via, written}>   ('folder_fail' when a write failed)
     *   batch.abort()
     *
     *   opts.callerReports — the tool reports failed files itself; no generic toast.
     *
     * ⚠️ Call it as the FIRST await in the click handler — see pickFolder().
     *
     *   opts.program — the batch is the tool's OWN output with a program-type
     *   extension (see __isProgramFile); otherwise programs are left out.
     */
    function openBatch(opts) {
        opts = opts || {};
        return Promise.resolve(openLane(opts)).then(function (batch) {
            return opts.program ? batch : withoutPrograms(batch);
        });
    }

    /* A program is not saved from this site (window.__isProgramFile in
       timbrica-core.js) — not into the picked folder, not inside the zip.
       The rest of the batch is delivered; one notice for the whole batch. */
    function withoutPrograms(batch) {
        if (!batch || !w.__isProgramFile) return batch;
        var withheld = 0, first = '', write = batch.write, finish = batch.finish;
        batch.write = function (name, blob) {
            if (w.__isProgramFile(name)) {
                if (!withheld++) first = name;
                return Promise.resolve({ name: name, withheld: true });
            }
            return write.apply(batch, arguments);
        };
        batch.finish = function () {
            if (withheld) w.__isProgramFile.refuse('batch', first, withheld);
            return Promise.resolve(finish.apply(batch, arguments)).then(function (r) {
                if (r && withheld) r.withheld = withheld;
                return r;
            });
        };
        return batch;
    }

    function openLane(opts) {
        var slug = opts.slug || (w.__TOOL_SLUG ||
            ((document.querySelector('meta[name="tool-slug"]') || {}).content) || '');
        var zipName = opts.zipName || ((slug || 'timbrica') + '-files.zip');
        var tel = function (via, extra) {
            try {
                if (w.__ae) w.__ae('feature_use', Object.assign(
                    { f: 'batch_deliver', via: via, slug: slug }, extra || {}));
            } catch (e) {}
        };

        var archive = function (why) {
            var held = [], bytes = 0;
            return {
                mode: 'archive',
                reason: why,
                write: function (name, blob) {
                    held.push({ name: name, blob: blob });
                    bytes += (blob && blob.size) || 0;
                    return Promise.resolve({ name: name });
                },
                abort: function () { held = []; },
                finish: function () {
                    if (!held.length) return Promise.resolve({ via: 'empty', written: 0 });
                    var items = held; held = [];
                    var gb = (bytes / 1073741824).toFixed(2) + ' GB';
                    // A source the browser can no longer read. Chromium past the
                    // model's ceiling — that is its blob store, and the «too big»
                    // text with its folder advice is the true one. Anywhere else
                    // the size is not the cause: Safari stopped reading a picked
                    // 5.5 GB take mid-batch (support chat 728, chat 714), or the
                    // file moved after the pick — the reading sentence says which.
                    var refuseUnreadable = function (err, cause) {
                        var over = measuredEngine() === 'chromium' && !fitsArchive(bytes);
                        var why = /^NotFoundError/.test(String(cause || '')) ? 'not_found' : 'browser';
                        try {
                            if (over) { if (w.__toast) w.__toast(deliverMsg('too_big', gb), 'error'); }
                            else {
                                var say = (typeof w.__unreadableFileMsg === 'function' && w.__unreadableFileMsg(why)) || '';
                                if (w.__handleError) w.__handleError(err || new Error('BATCH_UNREADABLE'), 'batch_unreadable', say ? { say: say } : undefined);
                                else if (say && w.__toast) w.__toast(say, 'error');
                            }
                        } catch (e) {}
                        tel('refused', { n: items.length, reason: 'unreadable', over: over ? 1 : 0, err: String(cause || '').slice(0, 40) });
                    };
                    return packZip(items, opts.onProgress, { onPack: opts.onPack }).then(function (zip) {
                        return Promise.resolve(w.__downloadFile
                            ? w.__downloadFile(zip, zipName, { src: 'batch_deliver' })
                            : null).then(function (ok) {
                            // The door answered «not now» — the slot of this gesture
                            // was spent, a save dialog was closed. The archive is the
                            // whole batch: it waits on the bar for one more press
                            // rather than being thrown away with this function.
                            var hold = w.__dlHold, parked = 0;
                            if (ok === false && hold && hold.singles) {
                                var there = false;
                                try { there = hold.peek().some(function (it) { return it.blob === zip; }); } catch (e) {}
                                if (!there) hold.add(zip, zipName, { single: true });
                                parked = 1;
                            }
                            tel('zip', { n: items.length, sizeKB: Math.round(zip.size / 1024), reason: why, parked: parked });
                            return { via: 'zip', written: items.length };
                        });
                    }, function (err) {
                        if (err && err.code === 'BATCH_UNREADABLE') {
                            refuseUnreadable(err, err.cause);
                            return { via: 'refused', written: 0 };
                        }
                        if (err && err.code === 'BATCH_TOO_BIG') {
                            return deliverInParts(items, zipName, opts.onPack).then(function (r) {
                                if (r && !r.failed) {
                                    // Files that travel as themselves are not «parts of
                                    // an archive»: the sentence names the press, not a zip.
                                    if (r.parts > 1) { try { if (w.__toast) w.__toast(r.bare === r.parts ? deliverMsg('singles', gb, r.parts) : deliverMsg('parts', gb, r.parts), 'info'); } catch (e) {} }
                                    tel('zip_parts', { n: items.length, parts: r.parts, bare: r.bare || 0, sizeKB: Math.round(bytes / 1024), reason: why });
                                    // Still `zip` to the caller: the batch is on
                                    // its way as archives, and every caller reads
                                    // that word as «delivered».
                                    return { via: 'zip', written: items.length, parts: r.parts };
                                }
                                if (r && r.failed === 'unreadable') {
                                    refuseUnreadable(null, r.cause);
                                    return { via: 'refused', written: 0 };
                                }
                                try { if (w.__toast) w.__toast(deliverMsg('too_big', gb), 'error'); } catch (e) {}
                                tel('refused', { n: items.length, reason: 'too_big', err: String((r && r.cause) || '').slice(0, 40) });
                                return { via: 'refused', written: 0 };
                            });
                        }
                        tel('zip_fail', { n: items.length, err: String((err && err.message) || err).slice(0, 40) });
                        try { if (w.__handleError) w.__handleError(err, { stage: 'batch_zip' }); } catch (e) {}
                        return { via: 'refused', written: 0 };
                    });
                }
            };
        };

        // opts.skipFolder — the caller has ALREADY asked and been declined
        // (__FsaSink.flushFallback). Asking again in the same breath is nagging.
        if (opts.skipFolder) return Promise.resolve(archive('caller_skipped'));
        // opts.archiveOnly — the button says «Download ZIP», so the answer is a
        // ZIP: no folder dialog appearing on some clicks and not others (the
        // activation may or may not have outlived the tool's own work).
        if (opts.archiveOnly) return Promise.resolve(archive('archive_only'));
        if (!folderAvailable()) return Promise.resolve(archive('no_folder_api'));
        // The picker needs the click's activation. A caller that awaited its
        // work first (encode, worker, network) no longer has one, and opening
        // a dialog that is going to be refused only adds a failure to the
        // console. Ask the browser instead of finding out the hard way.
        try {
            if (w.navigator && w.navigator.userActivation
                && w.navigator.userActivation.isActive === false) {
                return Promise.resolve(archive('no_activation'));
            }
        } catch (e) {}

        return pickFolder().then(function (sink) {
            // A closed dialog is an informed decision, so it degrades to ONE
            // archive — never to a loop of downloads, which the browser drops
            // past the first and which kills the tab for the whole origin.
            if (!sink) { tel('declined'); return archive('declined'); }
            var written = 0, failed = null;
            return {
                mode: 'folder',
                dir: sink.dir || null,
                write: function (name, blob) {
                    return sink.write(name, blob).then(function (r) {
                        written++;
                        if (opts.onProgress) { try { opts.onProgress(written, opts.total || 0); } catch (e) {} }
                        return r;
                    }, function (err) { failed = err; throw err; });
                },
                abort: function () { try { sink.dispose(); } catch (e) {} },
                finish: function () {
                    try { sink.dispose(); } catch (e) {}
                    if (failed) {
                        // Disk full, permission revoked mid-batch. Whatever
                        // landed stays; say so instead of silently switching
                        // lanes and writing everything a second time.
                        tel('folder_fail', { written: written, err: String((failed && failed.name) || failed).slice(0, 40) });
                        // opts.callerReports — the tool names every file that
                        // did not land in its own report; a generic toast on
                        // top would say the same thing worse.
                        if (!opts.callerReports) {
                            try { if (w.__handleError) w.__handleError(failed, { stage: 'batch_folder' }); } catch (e) {}
                        }
                        return Promise.resolve({ via: 'folder_fail', written: written });
                    }
                    tel('folder', { written: written });
                    return Promise.resolve({ via: 'folder', written: written });
                }
            };
        }, function () { return archive('picker_error'); });
    }

    /** Files already in hand: openBatch + write them all + finish. */
    function deliver(items, opts) {
        opts = opts || {};
        items = (items || []).filter(function (it) { return it && it.blob; });
        if (!items.length) return Promise.resolve({ via: 'empty', written: 0 });
        var o = {};
        for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k];
        o.total = items.length;
        return openBatch(o).then(function (batch) {
            var i = 0;
            var next = function () {
                if (i >= items.length) return batch.finish();
                var it = items[i++];
                return batch.write(it.name, it.blob).then(next);
            };
            return Promise.resolve(next()).catch(function () { return batch.finish(); });
        });
    }

    w.__batchDeliver = {
        folderAvailable: folderAvailable,
        liveBlobLimitBytes: liveBlobLimitBytes,
        archiveCeilingBytes: archiveCeilingBytes,
        materialiseCeilingBytes: materialiseCeilingBytes,
        fitsArchive: fitsArchive,
        pickFolder: pickFolder,
        packZip: packZip,
        deliver: deliver,
        openBatch: openBatch,
    };
})(window);
